import React, { useState } from 'react';
import { Form, Button, Container, Row, Col, Alert, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { sendForgotPasswordOTP, verifyForgotPasswordOTP, resetPasswordWithOTP } from '../services/NewForgotPasswordService';
import { validateEmail, validatePassword, validateConfirmPassword } from '../utils/validation';

const ForgetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'otp' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.message!);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await sendForgotPasswordOTP(email);
      setSuccess('OTP sent to your email successfully');
      setStep('otp');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await verifyForgotPasswordOTP(email, otp);
      setSuccess('OTP verified successfully! Please set your new password.');
      setStep('reset');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message!);
      return;
    }

    const confirmPasswordValidation = validateConfirmPassword(newPassword, confirmPassword);
    if (!confirmPasswordValidation.isValid) {
      setError(confirmPasswordValidation.message!);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await resetPasswordWithOTP(email, otp, newPassword, confirmPassword);
      setSuccess('Password reset successful! You can now login with your new password.');
      setTimeout(() => navigate('/signing'), 3000);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'email': return 'Forgot Password';
      case 'otp': return 'Verify OTP';
      case 'reset': return 'Set New Password';
      default: return 'Forgot Password';
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)',
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '8rem'
    }}>
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow-lg border-0" style={{ borderRadius: '15px' }}>
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <i className="bi bi-key" style={{ fontSize: '3rem', color: '#810081' }}></i>
                  </div>
                  <h3 className="fw-bold" style={{ color: '#810081' }}>
                    {getStepTitle()}
                  </h3>
                  <div className="mt-2">
                    <span className={`badge ${step === 'email' ? 'bg-primary' : 'bg-secondary'} me-1`}>1</span>
                    <span className={`badge ${step === 'otp' ? 'bg-primary' : 'bg-secondary'} me-1`}>2</span>
                    <span className={`badge ${step === 'reset' ? 'bg-primary' : 'bg-secondary'}`}>3</span>
                  </div>
                </div>

                {step === 'email' && (
                  <Form onSubmit={handleSendOTP}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your registered email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="py-2 border-2"
                        style={{ 
                          borderRadius: '10px', 
                          borderColor: '#FFD600',
                          backgroundColor: '#fefefe'
                        }}
                      />
                    </Form.Group>

                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-100 py-2 fw-semibold border-0 mb-3"
                      style={{
                        backgroundColor: '#FFD600',
                        color: '#810081',
                        borderRadius: '10px'
                      }}
                    >
                      {loading ? 'Sending...' : 'Send OTP'}
                    </Button>
                  </Form>
                )}

                {step === 'otp' && (
                  <Form onSubmit={handleVerifyOTP}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Verification Code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        maxLength={6}
                        required
                        className="py-2 border-2 text-center"
                        style={{ 
                          borderRadius: '10px', 
                          borderColor: '#FFD600',
                          backgroundColor: '#fefefe',
                          fontSize: '1.2rem',
                          letterSpacing: '0.2rem'
                        }}
                      />
                      <Form.Text className="text-muted">
                        OTP sent to: {email}
                      </Form.Text>
                    </Form.Group>

                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-100 py-2 fw-semibold border-0 mb-3"
                      style={{
                        backgroundColor: '#FFD600',
                        color: '#810081',
                        borderRadius: '10px'
                      }}
                    >
                      {loading ? 'Verifying...' : 'Verify OTP'}
                    </Button>
                  </Form>
                )}

                {step === 'reset' && (
                  <Form onSubmit={handleResetPassword}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold" style={{ color: '#810081' }}>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        className="py-2 border-2"
                        style={{ 
                          borderRadius: '10px', 
                          borderColor: '#FFD600',
                          backgroundColor: '#fefefe'
                        }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="py-2 border-2"
                        style={{ 
                          borderRadius: '10px', 
                          borderColor: '#FFD600',
                          backgroundColor: '#fefefe'
                        }}
                      />
                    </Form.Group>

                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-100 py-2 fw-semibold border-0 mb-3"
                      style={{
                        backgroundColor: '#FFD600',
                        color: '#810081',
                        borderRadius: '10px'
                      }}
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </Form>
                )}

                {success && (
                  <Alert variant="success" className="mt-3 border-0" style={{ borderRadius: '10px' }}>
                    <i className="bi bi-check-circle me-2"></i>
                    {success}
                  </Alert>
                )}

                {error && (
                  <Alert variant="danger" className="mt-3 border-0" style={{ borderRadius: '10px' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

                <div className="text-center mt-3">
                  <button 
                    type="button"
                    onClick={() => navigate('/signing')}
                    className="btn btn-link text-decoration-none p-0"
                    style={{ color: '#810081' }}
                  >
                    Back to Login
                  </button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ForgetPasswordPage;
